import React, { useState, useRef, useEffect, useCallback } from 'react';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMobileAlt,
  faBriefcase,
  faAt,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import Logo from '../components/Logo/Logo';
import Header from '../components/Header/Header';
import Navigation from '../components/Navigation/Navigation';
import ExpiryPopup from '../components/ExpiryPopup/ExpiryPopup';
import Footer from '../components/Footer/Footer';
import API from '../api';
import { API_RESPONSE_TYPES } from '../constants';
import { authData } from '../utils';
import ScrollMessage from '../components/ScrollMessage/ScrollMessage';


const StyledMain = styled.main`
  width: 95%;
  max-width: 1800px;
  height: auto;
  margin: 0 auto;
  padding: 0px;
`;

const StyledAuthMainOuter = styled.div`
  min-height: 100vh;
  width: 100%;
  background: ${({ landingPrimaryColor }) =>
    landingPrimaryColor
      ? `linear-gradient(135deg, ${landingPrimaryColor}, #0f172a)`
      : 'linear-gradient(135deg, #0f172a, #020617)'};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledAuthCenterWrapper = styled.div`
  width: 100%;
  max-width: 500px;
  padding: 20px;
`;

const StyledAuthCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 36px 32px;
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.18);
  animation: fadeIn 0.35s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(14px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const StyledAuthHeader = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 18px;

  img {
    max-height: 44px;
  }
`;

const StyledAuthTitle = styled.h1`
  text-align: center;
  font-size: 22px;
  font-weight: 600;
  color: #020617;
  margin-bottom: 6px;
`;

const StyledAuthSubtitle = styled.p`
  text-align: center;
  font-size: 14px;
  color: #64748b;
  margin-bottom: 26px;
`;

const StyledAuthFormIn = styled.div`
  width: 100%;
  padding: 0 8px;
  box-sizing: border-box;
`;

const AuthFormNormalize = styled.div`
  input,
  select,
  textarea,
  button {
    width: 100%;
    box-sizing: border-box;   /* 🔥 critical */
    padding-left: 14px;
    padding-right: 14px;      /* 🔥 equal padding */
    margin: 0;
  }
`;


const StyledMainSection = styled.section`
  background-color: rgb(255, 255, 255);
  width: 100%;
  max-width: 1200px;
  height: auto;
  margin: 0px;
  display: flex;
  flex-direction: row-reverse;
  column-gap: 30px;
  padding: 30px;
  border-radius: 30px;
  @media (max-width: 1000px) {
    width: 100%;
    display: block;
    padding: 10px 0px;
  }
`;


const StyledAuthFormSection = styled.section`
  width: 50%;
  height: auto;
  margin: 0px;
  padding: 0px;
  overflow-y: scroll;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  @media (max-width: 1000px) {
    width: 100%;
  }
  ::-webkit-scrollbar {
    display: none;
  }
`;

const StyledContentArea = styled.section`
  width: 50%;
  height: 100%;
  img {
    width: 100%;
    height: 100%;
    border-radius: 30px;
  }
  @media (max-width: 1000px) {
    display: none;
  }
`;

const StyledContantBlocksSection = styled.section`
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: row;
  row-gap: 25px;
  column-gap: 25px;
  flex-wrap: wrap;
  justify-content: center;
  align-content: center;
  margin: 0px;
  padding: 0px;
  @media (max-width: 1000px) {
    display: block;
    width: 92%;
    margin: 0 auto;
  }
`;

const StyledContentBlock = styled.section`
  width: calc(33.33% - 20px);
  min-height: 150px;
  height: auto;
  background-color: ${({ backgroundColor }) =>
    backgroundColor ? backgroundColor : '#0095ff'};
  border-radius: 15px;
  box-shadow: 0px 0px 50px -10px #d1d1d1;
  @media (max-width: 1000px) {
    width: 100%;
    margin: 15px auto;
  }
`;

const StyledContentHeading = styled.h2`
  font-size: 20px;
  font-weight: bold;
  color: #ffffff;
  margin: 20px 20px 10px;
  padding: 0px;
  @media (max-width: 1000px) {
    margin: 0px;
    padding: 15px 15px 0px;
  }
`;

const StyledContentPara = styled.p`
  font-size: 14px;
  color: ${({ color }) => (color ? color : '#ffffff')};
  margin: 0px 20px 20px;
  padding: 0px;
  line-height: 24px;
  @media (max-width: 1000px) {
    margin: 0px;
    padding: 15px 15px 0px 15px;
  }
`;

const StyledContactSection = styled.section`
  width: calc(100% - 50px);
  height: auto;
  padding: 0px;
  margin: 10px 0px 0px 15px;
`;

const StyledAddressContent = styled.section``;

const StyledPhoneContent = styled.section`
  display: flex;
  flex-direction: row;
  column-gap: 15px;
  @media (max-width: 1000px) {
    flex-direction: column;
  }
`;

const StyledContactInfo = styled.section`
  height: auto;
  display: flex;
  flex-direction: row;
  column-gap: 15px;
  margin-bottom: 5px;
`;

const StyledIcon = styled.section`
  font-size: 25px;
  color: #545454;
`;

const StyledContent = styled.section`
  h3 {
    font-size: 16px;
    color: #545454;
    margin: 0px 0px 10px 0px;
    padding: 0px;
    font-weight: normal;
  }
  address {
    font-size: 14px;
    color: #545454;
    font-weight: normal;
    font-style: normal;
    line-height: 24px;
  }
`;

const StyledNumberContent = styled.section`
  align-items: center;
  display: flex;
  a {
    font-size: 14px;
    text-decoration: none;
    color: #0095ff;
  }
`;

const StyledContactUsHeading = styled.h1`
  font-size: 24px;
  font-weight: normal;
  margin: 25px 15px 0px;
  padding: 0px;
  color: #000000;
  font-family: math;
`;

const StyledAboutUsText = styled.p`
  font-size: 14px;
  line-height: 24px;
  text-align: justify;
  margin: 10px 15px;
  color: #545454;
`;

// const withAuthHOC = (WrappedComponent) => {
//   return (props) => {
//     const {
//       appName,
//       appLogo,
//       firmName,
//       firmAddress,
//       appEmailAddress,
//       appPhoneNumber,
//       aboutUs,
//       landingPrimaryColor,
//       landingSecondaryColor,
//     } = props.appData;

//     return (
//       <>
//         <StyledAuthMainOuter landingPrimaryColor={landingPrimaryColor}>
//           <StyledMainSection>
//             <StyledAuthFormSection>
//               <StyledAuthHeader>
//                 <Logo src={appLogo} alt={appName} type="auth" />
//               </StyledAuthHeader>
//               <StyledAuthFormIn>
//                 <WrappedComponent {...props} />
//               </StyledAuthFormIn>
//             </StyledAuthFormSection>
//             <StyledContentArea>
//               <img src="img/misc/login-bg.png" alt="Login" />
//             </StyledContentArea>
//           </StyledMainSection>
//         </StyledAuthMainOuter>
//       </>
//     );
//   };
// };
const withAuthHOC = (WrappedComponent) => {
  return (props) => {
    const {
      appName,
      appLogo,
      landingPrimaryColor,
    } = props.appData || {};

    return (
      <StyledAuthMainOuter landingPrimaryColor={landingPrimaryColor}>
        <StyledAuthCenterWrapper>
          <StyledAuthCard>
            <StyledAuthHeader>
              <Logo src={appLogo} alt={appName} type="auth" />
            </StyledAuthHeader>

            <StyledAuthTitle>Welcome back</StyledAuthTitle>
            <StyledAuthSubtitle>
              Sign in to continue to {appName}
            </StyledAuthSubtitle>

            <StyledAuthFormIn>
              <WrappedComponent {...props} />
            </StyledAuthFormIn>
          </StyledAuthCard>
        </StyledAuthCenterWrapper>
      </StyledAuthMainOuter>
    );
  };
};

const withAppHOC = (WrappedComponent) => {
  return (props) => {
    const { accountData } = authData.get();
    const {
      appName,
      appLogo,
      appPhoneNumber,
      appEmailAddress,
      packageExpiryDate,
    } = props.appData;
    const expiryCheckedRef = useRef(false);
    const [showExpiryPopup, setShowExpiryPopup] = useState(false);
    const [daysLeft, setDaysLeft] = useState(0);
    const [showNav, setShowNav] = useState(false);
    const [walletBalance, setWalletBalance] = useState('-');
    const [creditDue, setCreditDue] = useState('-');
    const [minimumBalance, setMinimumBalance] = useState('-');
    const [profileName, setProfileName] = useState('-');
    const [profilePhoto, setProfilePhoto] = useState('');
    const [scrollMessage, setScrollMessage] = useState('');
    const [fcmToken, setFcmToken] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [authDomain, setAuthDomain] = useState('');
    const [projectId, setProjectId] = useState('');
    const [storageBucket, setStorageBucket] = useState('');
    const [messagingSenderId, setMessagingSenderId] = useState('');
    const [appId, setAppId] = useState('');
    const [measurementId, setMeasurementId] = useState('');
    const [vapidKey, setVapidKey] = useState('');
    const [notifications, setNotifications] = useState([]);
    const [accountsRole, setAccountsRole] = useState('');

    const onClickLogout = (e) => {
      e.preventDefault();
      const { authToken } = authData.get();
      API.delete(`/auth/sessions/?sessionToken=${authToken}`);
      authData.clear();
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (let registration of registrations) {
          registration.unregister();
        }
      });
      window.location.href = '/';
    };

    const saveFcm = useCallback(() => {
      API.post(`/auth/save-fcm`, { fcmToken });
    }, [fcmToken]);

    const readNotifications = useCallback(() => {
      API.get(`/configurations/notifications`).then((response) => {
        const { status, data } = response.data;
        if (status === API_RESPONSE_TYPES.SUCCESS) {
          setNotifications(data);
        }
      });
    }, []);

    const updateNotification = (id) => {
      API.put(`/configurations/notifications/${id}`).then((response) => {
        const { status, data } = response.data;
        if (status === API_RESPONSE_TYPES.SUCCESS) {
          setNotifications(data);
        }
      });
    };

    const readWalletBalance = useCallback(() => {
      API.get(`/auth/profile`).then((response) => {
        const { status, data } = response.data;
        if (status === API_RESPONSE_TYPES.SUCCESS) {
          setWalletBalance(data.walletBalance);
          setCreditDue(data.creditDue);
          setMinimumBalance(data.minimumBalance);
          setProfileName(`${data.firstName} ${data.lastName}` || 'N/A');
          setProfilePhoto(data.photo || '');
          setAccountsRole(data.accountsRole || '');
        }
      });
    }, []);

    const readScrollMessage = useCallback(() => {
      API.get(`/configurations/scroll-message`).then((response) => {
        const { status, data } = response.data;
        if (status === API_RESPONSE_TYPES.SUCCESS) {
          setScrollMessage(data);
        }
      });
    }, []);

    const onClickNavigationIcon = () => {
      setShowNav((showNav) => !showNav);
    };

    const readPublicFCMWebConfig = useCallback(() => {
      API.get(`/configurations/push-notifications-configuration/public`).then(
        (response) => {
          const { status, data } = response.data;
          if (status === API_RESPONSE_TYPES.SUCCESS) {
            const {
              apiKey,
              authDomain,
              projectId,
              storageBucket,
              messagingSenderId,
              appId,
              measurementId,
              vapidKey,
            } = data;
            setApiKey(apiKey ? apiKey : '');
            setAuthDomain(authDomain ? authDomain : '');
            setProjectId(projectId ? projectId : '');
            setStorageBucket(storageBucket ? storageBucket : '');
            setMessagingSenderId(messagingSenderId ? messagingSenderId : '');
            setAppId(appId ? appId : '');
            setMeasurementId(measurementId ? measurementId : '');
            setVapidKey(vapidKey ? vapidKey : '');
          }
        }
      );
    }, []);

    const initFirebase = useCallback(async () => {
      const app = await initializeApp({
        apiKey,
        authDomain,
        projectId,
        storageBucket,
        messagingSenderId,
        appId,
        measurementId,
      });

      const messaging = getMessaging(app);

      Notification.requestPermission().then((permission) => {
        if (permission === 'granted') {
          console.info('Notification User Permission Granted.');
          return getToken(messaging, {
            vapidKey,
          })
            .then((currentToken) => {
              if (currentToken) {
                console.info('FCM Token: ', currentToken);
                if (fcmToken !== currentToken) {
                  setFcmToken(currentToken);
                }
              } else {
                console.info('Failed to generate FCM token.');
              }
            })
            .catch((error) => {
              console.error(error);
            });
        } else {
          console.info('Notification User Permission Denied.');
        }
      });

      onMessage(
        messaging,
        (payload) => {
          console.info(payload);
          readNotifications();
        },
        (error) => {
          console.error(error);
        }
      );

      if ('serviceWorker' in navigator) {
        navigator.serviceWorker
          .register('/firebase-messaging-sw.js', {
            apiKey,
            authDomain,
            projectId,
            storageBucket,
            messagingSenderId,
            appId,
            measurementId,
          })
          .then((registration) => {
            console.info(
              'Registration successful, scope is:',
              registration.scope
            );
          })
          .catch((error) => {
            console.error(error);
          });
      }
    }, [
      fcmToken,
      apiKey,
      authDomain,
      projectId,
      storageBucket,
      messagingSenderId,
      appId,
      measurementId,
      vapidKey,
      readNotifications,
    ]);

    useEffect(() => {
      readWalletBalance();
      readScrollMessage();
      readPublicFCMWebConfig();
      readNotifications();
    }, [
      readWalletBalance,
      readScrollMessage,
      readPublicFCMWebConfig,
      readNotifications,
    ]);

    useEffect(() => {
      if (
        apiKey !== '' &&
        authDomain !== '' &&
        projectId !== '' &&
        storageBucket !== '' &&
        messagingSenderId !== '' &&
        appId !== '' &&
        measurementId !== '' &&
        vapidKey !== ''
      ) {
        initFirebase();
      }
    }, [
      apiKey,
      authDomain,
      projectId,
      storageBucket,
      messagingSenderId,
      appId,
      measurementId,
      vapidKey,
      initFirebase,
    ]);

    useEffect(() => {
      saveFcm();
    }, [fcmToken, saveFcm]);

    useEffect(() => {
      if (expiryCheckedRef.current) return;
      if (!accountData?.expiryDate) return;

      expiryCheckedRef.current = true;

      const today = new Date();
      const expiryDate = new Date(accountData.expiryDate);

      today.setHours(0, 0, 0, 0);
      expiryDate.setHours(0, 0, 0, 0);

      const diffTime = expiryDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays > 0 && diffDays <= 1) {
        const todayStr = today.toISOString().split('T')[0];
        const lastShownDate = localStorage.getItem('expiryPopupLastShown');

        if (lastShownDate !== todayStr) {
          setDaysLeft(diffDays);
          setShowExpiryPopup(true);
        }
      }
    }, [accountData?.expiryDate]);


    const closeExpiryPopup = () => {
      const todayStr = new Date().toISOString().split('T')[0];
      localStorage.setItem('expiryPopupLastShown', todayStr);
      setShowExpiryPopup(false);
    };


    return (
      <>
        <Header
          walletBalance={walletBalance}
          creditDue={creditDue}
          minimumBalance={minimumBalance}
          supportMobileNumber={appPhoneNumber}
          supportEmailAddress={appEmailAddress}
          fullName={profileName}
          profilePhoto={profilePhoto}
          onClickLogout={onClickLogout}
          appLogo={appLogo}
          appName={appName}
          packageExpiryDate={accountData.expiryDate}
          showNav={showNav}
          setShowNav={onClickNavigationIcon}
          notifications={notifications}
          updateNotification={updateNotification}
          commissionMode={accountData.commissionMode}
          commissionValue={accountData.commissionValue}
          accountsRole={accountsRole}
        />
        <Navigation showNav={showNav} />
        <ScrollMessage scrollMessage={scrollMessage} />
        <StyledMain>
          <WrappedComponent {...props} readWalletBalance={readWalletBalance} />
        </StyledMain>
        <Footer appName={appName} />
        {showExpiryPopup && (
          <ExpiryPopup
            daysLeft={daysLeft}
            onClose={closeExpiryPopup}
          />
        )}
      </>
    );
  };
};

export { withAuthHOC, withAppHOC };
